import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { resolveUrl, API_PREFIX } from 'src/core/constants/api.constants';
import { StateDefinitionDto } from 'src/core/models/state-definition/state-definition.dto';

@Injectable({ providedIn: 'root' })
export class StateDefinitionCacheService {
  private readonly http = inject(HttpClient);
  private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);
  private cachedDefinitions: StateDefinitionDto[] = [];
  private readonly definitions$: Observable<StateDefinitionDto[]> = this.refreshTrigger$.pipe(
    switchMap(() => this.fetchDefinitions()),
    tap(definitions => {
      this.cachedDefinitions = definitions;
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  getDefinitions(): Observable<StateDefinitionDto[]> {
    return this.definitions$;
  }

  getByName(name: string): StateDefinitionDto | undefined {
    return this.cachedDefinitions.find(d => d.name === name);
  }

  getByIdSync(id: number): StateDefinitionDto | undefined {
    return this.cachedDefinitions.find(d => d.id === id);
  }

  getAllSync(): StateDefinitionDto[] {
    return this.cachedDefinitions;
  }

  refresh(): void {
    this.refreshTrigger$.next();
  }

  private fetchDefinitions(): Observable<StateDefinitionDto[]> {
    return this.http
      .get<{ items: StateDefinitionDto[]; totalCount: number }>(
        resolveUrl(`${API_PREFIX}/state-definition?maxResultCount=100`),
      )
      .pipe(
        map(response => response.items),
        catchError(() => of([])),
      );
  }
}
